import type { ReactNode, RefObject } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { buildClassName } from 'utils/build-class-name';
import ListingsGrid from 'components/listings-grid';
import ProviderDisclaimer from 'components/provider-disclaimer';
import prioritizeFeeds from 'utils/prioritize-feeds';
import { useThemeContext } from 'contexts/theme';
import type { ListingCardData } from 'components/listing-card';
import type { ListingParams, Sort } from 'contexts/preferences/listing-params/types';
import type { InsightsResponse } from '@zoocasa/go-search';
import type { InsightsListingType } from 'data/insights/types';
import type { Breadcrumb } from 'components/breadcrumbs';
import { useFeaturesContext } from 'contexts/features';
import { useOnListingCardClick } from 'utils/listing-card';
import { PaginationMode } from 'components/control/pagination-nav';
import { testIds } from 'constants/test-constants';
import { Namespaces } from 'types/locales';
import { ListingsControls } from './listings-controls';
import ListingStatsBar from 'components/pages/hybrid/v2/components/listing-stats-bar';
import AreaFaq from 'components/pages/hybrid/v2/components/area-faq';
import styles from './listings-column.module.scss';

/**
 * Stats for the bar above the listing grid. `listingType` names the market `insights`
 * describes, since every market's stats share one shape; it picks the labels.
 */
export type ListingsStatsBarData = {
  insights?: InsightsResponse;
  listingType: InsightsListingType;
};

export type ListingsColumnProps = {
  heading: string;
  mobileHeading?: string;
  listings: readonly ListingCardData[];
  pagination: {
    page: number;
    size: number;
    count: number;
    total: number;
  };
  listingParams: ListingParams;
  footerSlot?: ReactNode;
  /** Optional action shown below the heading controls (used by mobile list view). */
  headerAction?: ReactNode;
  scrollRef?: RefObject<HTMLElement | null>;
  onListingHover?: (listingId: number | null) => void;
  onSortChanged?: (sort: Sort) => void;
  isFullScreen?: boolean;
  onSetFullScreen?: (fullScreen: boolean) => void;
  /** True when this column is showing as the mobile "list view" (the floating search bar above it is hidden), so its header needs extra top clearance. */
  isMobileListView?: boolean;
  isMapFullscreen?: boolean;
  onSetMapFullscreen?: (mapFullscreen: boolean) => void;
  hideViewToggle?: boolean;
  setPageNumber?: (pageNumber: number) => void;
  isLoading?: boolean;
  /** Area name (e.g. "Toronto, ON") used by the AEO insights FAQ section. */
  areaName?: string;
  /** Area's own info-page slug (e.g. "toronto-on") — backfills avg price/sqft in the stats bar. */
  citySlug?: string;
  /** Market insights for the current area (avg price, price/sqft, DOM, sell/list ratio). */
  insightsData?: InsightsResponse;
  /** The stats bar's market and its stats. The FAQ keeps `insightsData`, which is always for sale. */
  statsBar?: ListingsStatsBarData;
  breadcrumbs?: readonly Breadcrumb[];
  /** Stats bar "View More Stats" handler **/
  onMoreStatsClick?: () => void;
  /**
   * Renders each feed's compliance disclaimer directly below the pagination,
   * once per provider represented in the current page of results. Required for
   * US (and military base) areas — see the hybrid page, which gates the same
   * block on country and on there being listings to disclaim.
   */
  showProviderDisclaimer?: boolean;
};

export function ListingsColumn({
  heading,
  mobileHeading,
  listings,
  pagination,
  listingParams,
  footerSlot,
  headerAction,
  scrollRef,
  onListingHover,
  onSortChanged,
  isFullScreen = false,
  onSetFullScreen,
  isMobileListView = false,
  isMapFullscreen = false,
  onSetMapFullscreen,
  hideViewToggle = false,
  setPageNumber,
  isLoading = false,
  areaName,
  citySlug,
  insightsData,
  statsBar,
  breadcrumbs,
  onMoreStatsClick,
  showProviderDisclaimer = false,
}: ListingsColumnProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { isMobile } = useFeaturesContext();
  const { themeName } = useThemeContext();
  const onListingCardClick = useOnListingCardClick({ isMapSidePanel: true });

  // One disclaimer per feed the visible listings came from, priority feeds first
  // (the same `prioritizeFeeds` ordering the hybrid page and the search side
  // panel use).
  const providerIds = useMemo(() => {
    if (!showProviderDisclaimer) return [];
    return prioritizeFeeds([...new Set(listings.map(listing => listing.providerId))])
      .filter((providerId): providerId is number => providerId !== null);
  }, [showProviderDisclaimer, listings]);

  const handleSetPageNumber = (pageNumber: number) => {
    setPageNumber?.(pageNumber);
    if (scrollRef?.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return (
    <div
      data-testid="AreaPageV2-ListingsColumn"
      className={styles.root}
      ref={scrollRef as RefObject<HTMLDivElement>}
    >
      <div className={buildClassName(styles.header, isMobileListView && styles.headerMobileListView)}>
        <div className={styles.headingBlock}>
          <h1 className={styles.heading}>{isMobile && mobileHeading ? mobileHeading : heading}</h1>
          <p className={styles.subheading}>
            {t('area_page_v2.listings.map_area_count', {
              number: pagination.count,
              defaultValue: '{{number}} Listings in Map Area',
            })}
          </p>
        </div>
        <div className={styles.controlsSlot}>
          <ListingsControls
            sort={listingParams.sort}
            status={listingParams.filter.status}
            onSortChanged={onSortChanged}
            isFullScreen={isFullScreen}
            onSetFullScreen={onSetFullScreen}
            isMapFullscreen={isMapFullscreen}
            onSetMapFullscreen={onSetMapFullscreen}
            hideViewToggle={hideViewToggle}
          />
        </div>
      </div>
      {headerAction && (
        <div className={styles.headerAction}>{headerAction}</div>
      )}
      <div className={styles.statsBar}>
        {/* This column already has its own edges (no adjoining map gutter to bleed into), so
            isFullWidth keeps the bar's background flush with the column's own side padding. */}
        <ListingStatsBar
          insightsData={statsBar?.insights}
          listingType={statsBar?.listingType}
          citySlug={citySlug}
          onMoreStatsClick={onMoreStatsClick}
          isFullWidth
          className={styles.statsBarInner}
        />
      </div>
      <div className={styles.list}>
        <ListingsGrid
          testId={testIds.listingGrid}
          listings={listings}
          useListingCardWithCompliance={false}
          useListingCardV3
          isLoading={isLoading}
          params={{
            page: {
              number: pagination.page,
              size: pagination.size,
            },
          }}
          meta={{
            totalCount: pagination.count,
            totalPages: pagination.total,
          }}
          listingParams={listingParams}
          onListingCardClick={onListingCardClick}
          onListingHover={onListingHover}
          setPageNumber={handleSetPageNumber}
          isOnSidePanel={!isFullScreen}
          paginationMode={PaginationMode.NUMBERED}
          breadcrumbs={breadcrumbs ? [...breadcrumbs] : undefined}
        />
        {providerIds.length > 0 && (
          <div data-testid="AreaPageV2-ProviderDisclaimers">
            {providerIds.map(providerId => (
              <ProviderDisclaimer key={providerId} providerId={providerId} tenantName={themeName} />
            ))}
          </div>
        )}
        <AreaFaq areaName={areaName} listingCount={pagination.count} insightsData={insightsData} />
      </div>
      <div data-testid="AreaPageV2-FooterSlot" className={styles.footerSlot}>
        {footerSlot}
      </div>
    </div>
  );
}
